import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
	FieldSet,
} from "@/components/ui/field";
import { useForm } from "@/components/ui/form";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import { authClient } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	LockIcon,
	LogOutIcon,
	MonitorIcon,
	SmartphoneIcon,
	Trash2Icon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";

export function Settings() {
	const navigate = useNavigate();

	async function signOut() {
		await authClient.signOut();
		navigate("/sign-in", { replace: true });
	}

	return (
		<Shell className="flex flex-col gap-4">
			<h1 className="text-2xl font-bold tracking-tight text-balance">Settings</h1>
			<ChangePassword />
			<AuthSessionsCard />
			<Button
				variant="outline"
				onClick={signOut}
				className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
			>
				<LogOutIcon />
				Sign out
			</Button>
		</Shell>
	);
}

const passwordUpdateSchema = z
	.object({
		currentPassword: z
			.string("Current password is required")
			.min(8, "Password must be at least 8 characters long"),
		newPassword: z
			.string("New password is required")
			.min(8, "New password must be at least 8 characters long"),
		confirmNewPassword: z
			.string("Confirm new password is required")
			.min(8, "Confirm new password must be at least 8 characters long"),
	})
	.refine((data) => data.newPassword === data.confirmNewPassword, {
		message: "New passwords do not match",
		path: ["confirmNewPassword"],
	});

const ChangePassword = () => {
	const { mutate: changePassword, isPending } = useMutation({
		mutationFn: async ({
			currentPassword,
			newPassword,
		}: {
			currentPassword: string;
			newPassword: string;
		}) => {
			const result = await authClient.changePassword({
				currentPassword,
				newPassword,
				revokeOtherSessions: true,
			});
			if (result.error) {
				throw new Error(result.error.message ?? "Failed to change password");
			}
			return true;
		},
		onSuccess: () => {
			toast.success("Password changed successfully");
			form.reset();
		},
		onError: (error) => {
			toast.error(error.message ?? "Failed to change password");
		},
	});

	const form = useForm<z.infer<typeof passwordUpdateSchema>>({
		validators: {
			onSubmit: passwordUpdateSchema,
		},
		defaultValues: {
			currentPassword: "",
			newPassword: "",
			confirmNewPassword: "",
		},
		onSubmit: async ({ value }) => {
			changePassword({
				currentPassword: value.currentPassword,
				newPassword: value.newPassword,
			});
		},
	});
	return (
		<Card>
			<CardHeader>
				<CardTitle>Change Password</CardTitle>
				<CardDescription>Update your password</CardDescription>
			</CardHeader>
			<CardContent>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					id="change-password"
				>
					<FieldGroup>
						<FieldSet>
							<form.Field name="currentPassword">
								{(field) => (
									<Field>
										<FieldLabel htmlFor="currentPassword">
											Current Password
										</FieldLabel>
										<InputGroup>
											<InputGroupAddon>
												<LockIcon />
											</InputGroupAddon>
											<InputGroupInput
												id="currentPassword"
												value={field.state.value}
												type="password"
												onChange={(e) => field.handleChange(e.target.value)}
												onBlur={field.handleBlur}
												placeholder="•••••••••"
												disabled={isPending}
												className="h-8"
											/>
										</InputGroup>
										<FieldError errors={field.state.meta.errors} />
									</Field>
								)}
							</form.Field>
							<form.Field name="newPassword">
								{(field) => (
									<Field>
										<FieldLabel htmlFor="newPassword">New Password</FieldLabel>
										<InputGroup>
											<InputGroupAddon>
												<LockIcon />
											</InputGroupAddon>
											<InputGroupInput
												id="newPassword"
												value={field.state.value}
												type="password"
												onChange={(e) => field.handleChange(e.target.value)}
												onBlur={field.handleBlur}
												placeholder="•••••••••"
												disabled={isPending}
												className="h-8"
											/>
										</InputGroup>
										<FieldError errors={field.state.meta.errors} />
									</Field>
								)}
							</form.Field>
							<form.Field name="confirmNewPassword">
								{(field) => (
									<Field>
										<FieldLabel htmlFor="confirmNewPassword">
											Confirm New Password
										</FieldLabel>
										<InputGroup>
											<InputGroupAddon>
												<LockIcon />
											</InputGroupAddon>
											<InputGroupInput
												id="confirmNewPassword"
												value={field.state.value}
												type="password"
												onChange={(e) => field.handleChange(e.target.value)}
												onBlur={field.handleBlur}
												placeholder="•••••••••"
												disabled={isPending}
												className="h-8"
											/>
										</InputGroup>
										<FieldError errors={field.state.meta.errors} />
									</Field>
								)}
							</form.Field>
						</FieldSet>
					</FieldGroup>
				</form>
			</CardContent>
			<CardFooter className="grid">
				<form.Subscribe
					selector={(state) => [
						state.isSubmitting,
						state.isValid,
						state.isDirty,
					]}
				>
					{([isSubmitting, isValid, isDirty]) => (
						<Button
							type="submit"
							disabled={!isValid || !isDirty}
							loading={isSubmitting || isPending}
							form="change-password"
						>
							Update Password
						</Button>
					)}
				</form.Subscribe>
			</CardFooter>
		</Card>
	);
};

type AuthSession = {
	id: string;
	createdAt: string | Date;
	expiresAt: string | Date;
	token: string;
	ipAddress?: string | null;
	userAgent?: string | null;
};

function formatSessionDate(value: string | Date) {
	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return "Unknown";
	return date.toLocaleString();
}

function getSessionClientInfo(userAgent?: string | null) {
	const ua = userAgent?.toLowerCase() ?? "";
	const isMobile = /mobile|android|iphone|ipad|ipod/.test(ua);

	let browser = "Unknown browser";
	if (/edg\//.test(ua)) browser = "Edge";
	else if (/opr\//.test(ua)) browser = "Opera";
	else if (/firefox\//.test(ua)) browser = "Firefox";
	else if (/samsungbrowser\//.test(ua)) browser = "Samsung Internet";
	else if (/chrome\//.test(ua) || /crios\//.test(ua)) browser = "Chrome";
	else if (/safari\//.test(ua) && /version\//.test(ua)) browser = "Safari";
	else if (/msie|trident\//.test(ua)) browser = "Internet Explorer";

	return { browser, isMobile };
}

const AuthSessionsCard = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { data: currentSession } = authClient.useSession();
	const currentSessionToken = currentSession?.session?.token;

	const {
		data: sessions = [],
		isPending,
		error,
	} = useQuery({
		queryKey: ["auth-sessions"],
		queryFn: async (): Promise<AuthSession[]> => {
			const result = await authClient.listSessions();
			if (result.error) {
				throw new Error(result.error.message ?? "Failed to fetch sessions");
			}
			return result.data ?? [];
		},
	});

	const revokeSession = useMutation({
		mutationFn: async (token: string) => {
			const result = await authClient.revokeSession({ token });
			if (result.error) {
				throw new Error(result.error.message ?? "Failed to revoke session");
			}
			return token;
		},
		onSuccess: async (token) => {
			toast.success("Session revoked");
			await queryClient.invalidateQueries({ queryKey: ["auth-sessions"] });
			if (token === currentSessionToken) {
				navigate("/sign-in", { replace: true });
			}
		},
		onError: (err) => {
			toast.error(err.message ?? "Failed to revoke session");
		},
	});

	const revokeAllSessions = useMutation({
		mutationFn: async () => {
			const result = await authClient.revokeSessions();
			if (result.error) {
				throw new Error(
					result.error.message ?? "Failed to revoke all sessions",
				);
			}
			return true;
		},
		onSuccess: async () => {
			toast.success("All sessions revoked");
			await queryClient.invalidateQueries({ queryKey: ["auth-sessions"] });
			navigate("/sign-in", { replace: true });
		},
		onError: (err) => {
			toast.error(err.message ?? "Failed to revoke all sessions");
		},
	});

	return (
		<Card>
			<CardHeader>
				<CardTitle>Auth Sessions</CardTitle>
				<CardDescription>
					View active sessions and revoke access when needed.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-3">
				{isPending && (
					<p className="text-sm text-muted-foreground">Loading sessions...</p>
				)}
				{error && (
					<p className="text-sm text-destructive">
						{error.message ?? "Failed to load sessions"}
					</p>
				)}
				{!isPending && !error && sessions.length === 0 && (
					<p className="text-sm text-muted-foreground">
						No active sessions found.
					</p>
				)}
				{!isPending && !error && sessions.length > 0 && (
					<div className="space-y-2">
						{sessions.map((session) => {
							const isCurrent = session.token === currentSessionToken;
							const { browser, isMobile } = getSessionClientInfo(
								session.userAgent,
							);
							return (
								<div
									key={session.id}
									className={cn(
										"rounded-md border px-3 py-2",
										isCurrent && "border-primary bg-primary/5",
									)}
								>
									<div className="flex flex-wrap items-center justify-between gap-2">
										<div className="flex flex-wrap items-center gap-2">
											{isMobile ? (
												<SmartphoneIcon className="size-4 text-muted-foreground" />
											) : (
												<MonitorIcon className="size-4 text-muted-foreground" />
											)}
											<p className="text-sm font-medium">
												{browser}
											</p>
										</div>
										<Button
											size="icon-sm"
											variant="outline"
											onClick={() => revokeSession.mutate(session.token)}
											loading={
												revokeSession.isPending &&
												revokeSession.variables === session.token
											}
											aria-label="Revoke session"
											title="Revoke session"
										>
											<Trash2Icon />
										</Button>
									</div>
									<p className="mt-1 text-xs text-muted-foreground tabular-nums">
										IP: {session.ipAddress ?? "Unknown"} · Created:{" "}
										{formatSessionDate(session.createdAt)}
									</p>
								</div>
							);
						})}
					</div>
				)}
			</CardContent>
			<CardFooter className="grid">
				<Button
					variant="destructive"
					onClick={() => revokeAllSessions.mutate()}
					loading={revokeAllSessions.isPending}
					disabled={isPending || sessions.length === 0}
				>
					Revoke All Sessions
				</Button>
			</CardFooter>
		</Card>
	);
};
